import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CalendarClock,
  MapPin,
  MapPinOff,
  MapPinPlus,
  Navigation,
  Unplug,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber, formatRelativeDays } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { SystemChip } from '@/components/ui/system-chip';
import { MapboxProjectsMap } from './MapboxProjectsMap';
import {
  buildPlaces,
  groupPlacesByCity,
  mapboxToken,
  PIN_LEGEND,
  PIN_TONE_COLOR,
  pinCoverage,
  pinOf,
  projectLabel,
  statusLabel,
  wazeLinkFor,
} from './mapModel';
import {
  cleanText,
  daysLate,
  endDateDays,
  matchesStatusTab,
  plural,
  projectHref,
  projectStatusLabel,
  safeExternalUrl,
  stageSummaryOf,
} from './projectsModel';

/*
  Projects › Map view (03 #10, wave 3): the same filtered projects as the list, by city, with their map pins.

  With a Mapbox key (REACT_APP_MAPBOX_ACCESS_TOKEN): a list by city on the left (hover a row → its pin
  rises; click → the pin card) and the Mapbox map on the right — pins coloured by status, the systems of one
  site on one pin, nearby pins in numbered bubbles, a card with Open project / Navigate in Waze.
  Without a key: a "not set up" note and the same projects as cards by city, each with its Waze link.
  Both: "18 of 45 on the map · 27 without a pin → Add missing pins" (the quick pin list, AddPinsPanel).
*/

const count = (value) => formatNumber(value, { maxDecimals: 0 });
const OTHER_TAB_KEY = 'other:';
// Below md the map sits above the list: picking a row lower down scrolls the map (and its card) into view.
const STACKED_QUERY = '(max-width: 767.98px)';
const INTEGRATION_ADMIN_ROLES = new Set(['Admin', 'Super Admin']);

/** Where a place's pin sends people: its own Waze link when it has a pin, else one built from the pin. */
function wazeHref(place) {
  const pinned = place.projects.find((project) => pinOf(project));
  if (!pinned) return '';
  return safeExternalUrl(pinned.waze_url) || wazeLinkFor(pinOf(pinned));
}

function Dot({ tone, className }) {
  return (
    <span
      className={cn('inline-block h-[7px] w-[7px] shrink-0 rounded-full', className)}
      style={{ background: PIN_TONE_COLOR[tone] || PIN_TONE_COLOR.gray }}
      aria-hidden="true"
    />
  );
}

function stageText(project) {
  const summary = stageSummaryOf(project);
  if (!summary) return '';
  if (summary.complete) return 'All stages done';
  return `${summary.current_stage} ${summary.current_index}/${summary.count}`;
}

/** "48 d late · due 13 Aug 2026" (red) / "Due 31 Dec 2026 · 92 d left" for an Active project with an end date. */
function DueLine({ project, now }) {
  if (endDateDays(project, { now }) === null) return null;
  const late = daysLate(project, { now });
  if (late) {
    return (
      <p className="inline-flex items-center gap-1 text-[12px] font-semibold text-status-red-ink">
        <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
        {formatRelativeDays(project.end_date, { style: 'due', now })} · due {formatDate(project.end_date)}
      </p>
    );
  }
  return (
    <p className="text-[12px] text-ink-3">
      Due {formatDate(project.end_date)}
      {matchesStatusTab(project, 'Active') ? ` · ${formatRelativeDays(project.end_date, { style: 'due', now })}` : ''}
    </p>
  );
}

/** The pin card: one project (system, team · stage, due, Open project + Waze) or a site's systems. */
export function PlaceCard({ place, now, outsideTab }) {
  const waze = wazeHref(place);
  const single = place.projects.length === 1 ? place.projects[0] : null;
  return (
    <div className="flex flex-col gap-1.5" data-testid="map-place-card">
      <div className="flex min-w-0 items-center gap-2 pr-7">
        {single ? <SystemChip systemType={single.system_type} size="sm" /> : null}
        <span className="min-w-0 truncate text-[14px] font-bold text-ink" title={place.name}>{place.name}</span>
        {!single ? <StatusPill tone="navy" label={`${place.projects.length} systems`} size="sm" hideDot className="shrink-0" /> : null}
      </div>
      {single ? (
        <>
          <p className="truncate text-[12px] text-ink-2">
            {[cleanText(single.project_engineer) || 'No engineer', stageText(single)].filter(Boolean).join(' · ')}
          </p>
          <DueLine project={single} now={now} />
          {!matchesStatusTab(single, 'Active') ? (
            <span><StatusPill status={single.status} domain="project" label={projectStatusLabel(single.status) || undefined} size="sm" /></span>
          ) : null}
        </>
      ) : (
        <ul className="-mx-1 flex flex-col">
          {place.projects.map((project) => (
            <li key={project.id}>
              <Link
                to={projectHref(project)}
                className="flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`map-card-project-${project.id}`}
              >
                <SystemChip systemType={project.system_type} size="xs" />
                <span className="min-w-0 flex-1 truncate text-[12px] text-ink-2">
                  {[cleanText(project.project_engineer) || 'No engineer', stageText(project)].filter(Boolean).join(' · ')}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {outsideTab ? <p className="text-[11.5px] text-ink-3">{statusLabel(place.projects[0].status)} — outside this tab</p> : null}
      <div className="mt-1 flex items-center gap-2">
        {single ? (
          <Button asChild size="sm">
            <Link to={projectHref(single)} data-testid="map-card-open">Open project</Link>
          </Button>
        ) : null}
        {waze ? (
          <Button asChild size="sm" variant="outline">
            <a href={waze} target="_blank" rel="noopener noreferrer" data-testid="map-card-waze">
              <Navigation aria-hidden="true" />
              {single ? 'Waze' : 'Navigate in Waze'}
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function MapLegend({ fadedNote }) {
  return (
    <div className="pointer-events-none absolute bottom-2.5 left-2.5 z-20 flex flex-col gap-0.5 rounded-[10px] border border-line bg-surface/95 px-2.5 py-1.5 shadow-card" data-testid="map-legend">
      <div className="flex items-center gap-3 text-[12px] font-semibold text-ink-2">
        {PIN_LEGEND.map((item) => (
          <span key={item.tone} className="inline-flex items-center gap-1.5"><Dot tone={item.tone} className="h-2 w-2" />{item.label}</span>
        ))}
      </div>
      {fadedNote ? <span className="text-[11px] text-ink-3">{fadedNote}</span> : null}
    </div>
  );
}

/** "27 of 45 projects have no pin yet · Add pins" */
function CoverageCallout({ coverage, canEdit, onAddPins, className }) {
  if (!coverage.missing) return null;
  return (
    <div className={cn('flex items-center gap-2 rounded-[10px] bg-status-amber-bg px-2.5 py-2 text-[12px] text-ink-2', className)} data-testid="map-coverage-callout">
      <MapPinOff className="h-3.5 w-3.5 shrink-0 text-status-amber-ink" aria-hidden="true" />
      <span className="min-w-0">
        <b className="font-semibold text-ink">{count(coverage.missing)} of {count(coverage.total)}</b> {coverage.total === 1 ? 'project has' : 'projects have'} no map pin
      </span>
      {canEdit ? (
        <button type="button" onClick={() => onAddPins()} className="ml-auto shrink-0 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="map-add-pins-link">
          Add pins
        </button>
      ) : null}
    </div>
  );
}

/** Left of the map: the places by city. Hover → the pin rises; click → its card (no pin: Add pin). */
function PlaceList({ groups, coverage, hoveredKey, selectedKey, onHover, onSelect, canEdit, onAddPins }) {
  return (
    <div className="min-h-0 overflow-y-auto p-3" data-testid="map-place-list">
      <CoverageCallout coverage={coverage} canEdit={canEdit} onAddPins={onAddPins} className="mb-1" />
      {groups.map((group) => (
        <section key={group.city || 'none'} aria-label={`${group.label}: ${plural(group.projects, 'project')}`} data-testid={`map-city-${group.city || 'none'}`}>
          <h3 className="flex items-center gap-1.5 px-2 pb-1 pt-2.5 text-[12px] font-bold text-ink-3">
            {group.label} · {count(group.projects)}
          </h3>
          <ul className="flex flex-col gap-px">
            {group.places.map((place) => {
              const pinned = Boolean(place.pin);
              const active = place.key === selectedKey;
              const meta = place.projects.length > 1 ? `${place.projects.length} systems` : (place.systems[0] || '');
              return (
                <li key={place.key}>
                  <button
                    type="button"
                    onClick={() => {
                      if (pinned) onSelect(active ? null : place.key);
                      else if (canEdit) onAddPins(place.missing.map((project) => project.id));
                    }}
                    onMouseEnter={() => onHover(place.key)}
                    onMouseLeave={() => onHover(null)}
                    onFocus={() => onHover(place.key)}
                    onBlur={() => onHover(null)}
                    disabled={!pinned && !canEdit}
                    aria-pressed={pinned ? active : undefined}
                    title={pinned ? `Show ${place.name} on the map` : `${place.name} has no map pin yet${canEdit ? ' — add one' : ''}`}
                    className={cn(
                      'flex h-8 w-full min-w-0 items-center gap-2 rounded-lg px-2 text-left text-[13px] font-semibold text-ink transition-colors max-md:h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
                      active ? 'bg-brand-soft shadow-[inset_0_0_0_1px_var(--brand-soft-border)]' : place.key === hoveredKey ? 'bg-surface-hover' : 'hover:bg-surface-hover',
                      !pinned && 'text-ink-2',
                    )}
                    data-testid={`map-place-${place.key}`}
                  >
                    <Dot tone={place.tone} />
                    <span className="min-w-0 flex-1 truncate">{place.name}</span>
                    {pinned ? (
                      <span className="shrink-0 text-[12px] font-medium text-ink-3">{meta}</span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-ink-3">
                        <MapPinOff className="h-3 w-3" aria-hidden="true" />
                        {canEdit ? 'Add pin' : 'No pin'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** The "not set up" note of the map (a build-time Mapbox key, not a server integration). */
export function MapNotConfigured({ className }) {
  const { user } = useAuth() || {};
  const manage = INTEGRATION_ADMIN_ROLES.has(cleanText(user?.role));
  return (
    <div
      role="note"
      data-testid="map-not-configured"
      data-status="not_configured"
      className={cn('flex items-start gap-2.5 rounded-control border border-dashed border-[color:var(--line-dashed)] bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2', className)}
    >
      <Unplug className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <b className="font-semibold text-ink">The map is not set up yet</b>
          <StatusPill size="sm" tone="gray" label="Not set up" />
        </div>
        <p className="mt-0.5 text-ink-3">
          {manage
            ? <>IT adds a Mapbox key (<code className="text-[12px]">REACT_APP_MAPBOX_ACCESS_TOKEN</code>) to the web app build — see docs/INTEGRATIONS_SETUP.md › Projects map (Mapbox). </>
            : 'Ask IT or your administrator to set it up. '}
          Until then the projects are listed by city, each with its Waze link.
        </p>
      </div>
    </div>
  );
}

/** Without a Mapbox key: the projects as cards by city, each row with Waze / Add pin and its systems. */
function CityBoard({ groups, canEdit, onAddPins, now }) {
  return (
    <div className="gap-3 lg:columns-2" data-testid="map-city-board">
      {groups.map((group) => (
        <section
          key={group.city || 'none'}
          className="mb-3 min-w-0 break-inside-avoid rounded-xl border border-line bg-surface"
          aria-label={`${group.label}: ${plural(group.projects, 'project')}`}
          data-testid={`map-city-${group.city || 'none'}`}
        >
          <header className="flex items-baseline gap-2 border-b border-line-2 px-3.5 py-2.5">
            <h3 className="text-[14px] font-bold text-ink">{group.label}</h3>
            <span className="text-[12px] text-ink-3">
              {plural(group.places.length, 'site')} · {plural(group.projects, 'project')}
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-ink-3">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {count(group.pinned)} of {count(group.projects)} pinned
            </span>
          </header>
          <ul className="divide-y divide-line-2">
            {group.places.map((place) => {
              const waze = wazeHref(place);
              const single = place.projects.length === 1 ? place.projects[0] : null;
              const late = single ? daysLate(single, { now }) : 0;
              return (
                <li key={place.key} className="flex min-w-0 items-center gap-2.5 px-3.5 py-2" data-testid={`map-place-${place.key}`}>
                  <Dot tone={place.tone} />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {single ? (
                        <Link to={projectHref(single)} className="min-w-0 truncate rounded-sm text-[13.5px] font-semibold text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          {place.name}
                        </Link>
                      ) : (
                        <span className="min-w-0 truncate text-[13.5px] font-semibold text-ink">{place.name}</span>
                      )}
                      {place.projects.map((project) => (single ? (
                        <SystemChip key={project.id} systemType={project.system_type} size="xs" />
                      ) : (
                        <Link
                          key={project.id}
                          to={projectHref(project)}
                          className="shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`Open ${projectLabel(project)}`}
                          title={`Open ${projectLabel(project)}`}
                        >
                          <SystemChip systemType={project.system_type} size="xs" />
                        </Link>
                      )))}
                    </div>
                    <p className="truncate text-[12px] text-ink-3">
                      {single ? (
                        <>
                          {[cleanText(single.project_engineer) || 'No engineer', stageText(single)].filter(Boolean).join(' · ')}
                          {late ? <span className="font-semibold text-status-red-ink"> · {formatRelativeDays(single.end_date, { style: 'due', now })}</span> : null}
                          {!matchesStatusTab(single, 'Active') ? ` · ${statusLabel(single.status)}` : ''}
                        </>
                      ) : (
                        [cleanText(place.client), plural(place.projects.length, 'system')].filter(Boolean).join(' · ')
                      )}
                    </p>
                  </div>
                  {waze ? (
                    <Button asChild size="xs" variant="outline" className="shrink-0 max-md:h-10 max-md:px-3">
                      <a href={waze} target="_blank" rel="noopener noreferrer" aria-label={`Navigate to ${place.name} in Waze`} data-testid={`map-waze-${place.key}`}>
                        <Navigation aria-hidden="true" />
                        Waze
                      </a>
                    </Button>
                  ) : canEdit ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="ghost"
                      className="shrink-0 text-brand-ink max-md:h-10 max-md:px-3"
                      onClick={() => onAddPins(place.missing.map((project) => project.id))}
                      aria-label={`Add a map pin for ${place.name}`}
                      data-testid={`map-add-pin-${place.key}`}
                    >
                      <MapPinPlus aria-hidden="true" />
                      Add pin
                    </Button>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[12px] text-ink-3"><MapPinOff className="h-3 w-3" aria-hidden="true" />No pin</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * projects: the filtered list on this status tab; others: the same filters on the other tabs (faded pins);
 * tabLabel: 'Active' | 'All statuses' …; onAddPins(ids?) opens the quick pin list (ids = start with these).
 */
export function ProjectsMapView({ projects, others = [], tabLabel, canEdit, onAddPins, now, empty }) {
  const token = mapboxToken();
  const [hoveredKey, setHoveredKey] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [focus, setFocus] = useState(null); // { key, seq }: a row picked in the list → the map goes to its pin
  const mapBoxRef = useRef(null);
  const pickFromList = (key) => {
    setSelectedKey(key);
    if (!key) return;
    setFocus((previous) => ({ key, seq: (previous?.seq || 0) + 1 }));
    const box = mapBoxRef.current;
    if (box && typeof window !== 'undefined' && window.matchMedia?.(STACKED_QUERY)?.matches) {
      // The app header is sticky: the map counts as hidden once it slides under it.
      const header = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--app-header-height')) || 56;
      const { top, bottom } = box.getBoundingClientRect();
      if (top < header || bottom > window.innerHeight) box.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  };
  const places = useMemo(() => buildPlaces(projects), [projects]);
  const groups = useMemo(() => groupPlacesByCity(places), [places]);
  const coverage = useMemo(() => pinCoverage(projects), [projects]);
  // Pale pins (the other status tabs) live in their own key space: Nobu Hotel can have 2 Active systems here
  // and 2 Completed ones there — two places, two pins, two cards.
  const fadedPlaces = useMemo(
    () => buildPlaces(others).filter((place) => place.pin).map((place) => ({ ...place, key: `${OTHER_TAB_KEY}${place.key}` })),
    [others],
  );
  const fadedKeys = useMemo(() => new Set(fadedPlaces.map((place) => place.key)), [fadedPlaces]);
  const total = projects.length;

  const subtitle = `Same filters as the list · ${tabLabel} · ${plural(total, 'project')}${token && total ? ' · pick a row to find its pin' : ''}`;
  const addButton = canEdit && coverage.missing ? (
    <Button type="button" size="sm" variant="outline" onClick={() => onAddPins()} data-testid="map-add-missing-pins">
      <MapPinPlus aria-hidden="true" />
      Add missing pins ({count(coverage.missing)})
    </Button>
  ) : null;

  return (
    <section className="min-w-0 rounded-card border border-line bg-surface shadow-card" aria-labelledby="projects-map-title" data-testid="projects-map-view" data-map={token ? 'mapbox' : 'not-configured'}>
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line-2 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h2 id="projects-map-title" className="font-heading text-[15px] font-bold text-ink">Map view</h2>
          <p className="mt-0.5 text-[12.5px] text-ink-3" data-testid="map-subtitle">
            {subtitle}
            {total ? (
              <span data-testid="map-coverage">
                {' · '}<b className="font-semibold text-ink-2">{count(coverage.pinned)} of {count(total)}</b> on the map
                {coverage.missing ? <>{' · '}{count(coverage.missing)} without a pin</> : null}
              </span>
            ) : null}
          </p>
        </div>
        {addButton}
      </header>

      {!total ? (
        empty
      ) : token ? (
        <div className="flex flex-col md:grid md:h-[clamp(480px,calc(100vh-290px),760px)] md:grid-cols-[300px_minmax(0,1fr)]">
          <div className="order-2 min-h-0 border-t border-line-2 md:order-1 md:flex md:flex-col md:border-r md:border-t-0">
            <PlaceList
              groups={groups}
              coverage={coverage}
              hoveredKey={hoveredKey}
              selectedKey={selectedKey}
              onHover={setHoveredKey}
              onSelect={pickFromList}
              canEdit={canEdit}
              onAddPins={onAddPins}
            />
          </div>
          <div ref={mapBoxRef} className="relative order-1 m-3 h-[340px] scroll-mt-[calc(var(--app-header-height,56px)+12px)] md:order-2 md:h-auto">
            <MapboxProjectsMap
              token={token}
              places={places}
              faded={fadedPlaces}
              hoveredKey={hoveredKey}
              selectedKey={selectedKey}
              onHover={setHoveredKey}
              onSelect={setSelectedKey}
              focus={focus}
              renderCard={(place) => <PlaceCard place={place} now={now} outsideTab={fadedKeys.has(place.key)} />}
              className="absolute inset-0"
            />
            <MapLegend fadedNote={fadedPlaces.length ? `Pale pins are outside the ${tabLabel} tab` : ''} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 p-3 pb-0 sm:p-4 sm:pb-1">
          <MapNotConfigured />
          <CoverageCallout coverage={coverage} canEdit={canEdit} onAddPins={onAddPins} />
          <CityBoard groups={groups} canEdit={canEdit} onAddPins={onAddPins} now={now} />
        </div>
      )}
    </section>
  );
}

export default ProjectsMapView;
